import { request } from "./client";

export const getUser = () => request("/profiles");
export const convertCptToCis = (cptAmount, idempotencyKey) =>
  request("/profiles/convert-cpt-to-cis", {
    method: "POST",
    body: JSON.stringify({ cptAmount, idempotencyKey }),
  });

function normalizeSuggestedUser(raw) {
  const item = raw?.data ?? raw ?? {};
  const profile = item.profile ?? {};
  const id = item.id ?? profile.id ?? item.userId ?? profile.userId ?? null;
  const username =
    item.username ??
    profile.username ??
    item.handle ??
    profile.handle ??
    "";
  const fullName =
    item.fullName ??
    profile.fullName ??
    item.name ??
    profile.name ??
    "";
  const avatar =
    item.avatarUrl ??
    profile.avatarUrl ??
    item.avatar ??
    profile.avatar ??
    "";

  return {
    id,
    name: fullName || username,
    handle: username ? `@${username}` : "",
    avatar,
    following: item.isFollowing ?? profile.isFollowing ?? false,
    followers:
      item.followersCount ??
      item.followers ??
      profile.followersCount ??
      0,
  };
}

export const getSuggestedUsers = async () => {
  try {
    const res = await request("/profiles/all");
    const list = res?.data ?? res ?? [];
    const users = Array.isArray(list)
      ? list
      : Array.isArray(res?.items)
        ? res.items
        : [];

    const normalized = users.map(normalizeSuggestedUser).filter((u) => u.id);

    // Shuffle (Fisher-Yates) so a different random set of 4 shows up on
    // every load, instead of dumping the entire platform user list.
    for (let i = normalized.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [normalized[i], normalized[j]] = [normalized[j], normalized[i]];
    }

    return normalized.slice(0, 4);
  } catch (err) {
    console.error("Failed to load suggested users:", err);
    return [];
  }
};

export const followUser = (profileId) =>
  request(`/profiles/${profileId}/follow`, { method: "POST" });

export const unfollowUser = (profileId) =>
  request(`/profiles/${profileId}/follow`, { method: "DELETE" });

export const checkFollowStatus = async (profileId) => {
  try {
    await followUser(profileId);
    await unfollowUser(profileId).catch(() => {});
    return false;
  } catch (err) {
    if (err?.status === 400 || err?.message?.includes("400")) return true;
    return false;
  }
};

function readStatValue(value, preferredKeys = []) {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (value && typeof value === "object") {
    for (const key of preferredKeys) {
      const candidate = value[key];
      if (typeof candidate === "number" && Number.isFinite(candidate)) return candidate;
    }
    const fallback =
      value.totalScore ??
      value.score ??
      value.value ??
      value.currentStreak ??
      value.count ??
      value.amount ??
      value.points ??
      value.days ??
      null;
    return typeof fallback === "number" && Number.isFinite(fallback) ? fallback : 0;
  }
  const numeric = Number(value);
  return Number.isFinite(numeric) ? numeric : 0;
}

function normalizeProfile(raw) {
  const p = raw?.data ?? raw ?? {};
  const prof = p.profile ?? p.user?.profile ?? p.author?.profile ?? {};
  const u = p.user ?? p.author ?? p.authorProfile ?? {};
  const stat = p.userStat ?? p.userStats ?? p.stats ?? prof.userStat ?? u.userStat ?? {};
  const socialLinks = p.socialLinks ?? prof.socialLinks ?? u.socialLinks ?? {};

  const cisScore = readStatValue(
    p.cisScore ?? stat.cisScore ?? stat.cis ?? p.influenceScore ?? stat.influenceScore,
    ["totalScore", "score", "value"],
  );
  const streak = readStatValue(
    p.streak ?? stat.streak ?? stat.currentStreak ?? stat.streakDays,
    ["currentStreak", "value", "count", "days"],
  );

  // CPT lives at userStat.cisScore.totalCptEarned in the backend /profiles response.
  // There is no /wallet endpoint — read CPT directly from the profile.
  const cisScoreObj = stat.cisScore ?? {};
  const cptEarned = readStatValue(
    p.cptEarned ?? p.totalCptEarned ?? cisScoreObj.totalCptEarned ?? 0,
    ["totalCptEarned", "amount", "value"],
  );

  return {
    ...p,
    id: p.id ?? prof.id ?? u.id ?? p.userId ?? p.authorId ?? null,
    userId: p.userId ?? p.id ?? prof.id ?? u.id ?? null,
    fullName: p.fullName ?? prof.fullName ?? u.fullName ?? p.name ?? u.name ?? "",
    username: p.username ?? prof.username ?? u.username ?? "",
    avatarUrl: p.avatarUrl ?? prof.avatarUrl ?? u.avatarUrl ?? p.avatar ?? u.avatar ?? "",
    bio: p.bio ?? prof.bio ?? u.bio ?? "",
    socialLinks,
    followers: p.followers ?? p.followersCount ?? p._count?.followers ?? 0,
    following: p.following ?? p.followingCount ?? p._count?.following ?? 0,
    isFollowing: p.isFollowing ?? p.isFollowed ?? p.viewerIsFollowing ?? false,
    cltHeld: p.cltHeld ?? p.cltHeldAmount ?? p.clt ?? 0,
    cisScore,
    streak,
    cptEarned,
  };
}

export const getProfile = (userId) => {
  const own = () => request("/profiles").then((raw) => normalizeProfile(raw));

  if (!userId) return own();

  return request(`/profiles/${userId}`)
    .then((raw) => normalizeProfile(raw))
    .catch(() =>
      request("/profiles/all").then((raw) => {
        const list = raw?.data ?? raw ?? [];
        const arr = Array.isArray(list) ? list : [];
        const p = arr.find((u) => u.id === userId || u.userId === userId) ?? {};
        if (!p.id && !p.userId) return own();
        return normalizeProfile(p);
      }),
    );
};

export const createProfile = (_userId, payload) =>
  request("/profiles", { method: "PATCH", body: JSON.stringify(payload) });

export const updateProfile = (_userId, payload) =>
  request("/profiles", { method: "PATCH", body: JSON.stringify(payload) });

export const applyReferral = (referralCode) =>
  request("/profiles/referral/apply", {
    method: "POST",
    body: JSON.stringify({ referralCode }),
  });
